import Cart from "src/core/checkout/models/cart";
import TymberItem from "./cartItem";
import {findRelation} from "../../api/utils";
import autoBind from "auto-bind";
import {deepClone} from "src/core/common/utils";
import Orderable from "./orderable";
import get from "lodash/get";
import DeliverySpecification from "src/core/common/models/deliverySpecification";

class TymberCart extends Cart {
  cachedItems = null;

  constructor(cartObject, cartPricesObject = {}) {
    super(cartObject);
    this.cartPricesObject = cartPricesObject;
    Object.freeze(cartPricesObject);
    this.orderable = new Orderable(cartObject, cartPricesObject);
    autoBind(this);
  }

  getId() {
    return this.cartObject.data.id;
  }
  getInventoryType() {
    return get(this.cartObject, "data.attributes.inventory_type");
  }

  getItems() {
    if (!this.cachedItems) {
      const products = this.cartObject.data.relationships?.products?.data || [];
      this.cachedItems = products.map(relation => {
        const item = findRelation(this.cartObject.included, relation);
        return new TymberItem(
          {
            data: item,
            included: this.cartObject.included,
          },
          this.getItemPrices(item.id)
        );
      });
    }
    return this.cachedItems;
  }
  getNumberOfItems() {
    let items = this.cachedItems;
    if (!items) {
      items = this.getItems();
    }
    return items.reduce((acc, item) => acc + item.getQuantity(), 0);
  }
  getItemPrices(itemId) {
    if (this.cartPricesObject && this.cartPricesObject.included) {
      return (
        findRelation(this.cartPricesObject.included, {
          id: itemId,
          type: "cart_item_prices",
        }) || {}
      );
    } else {
      return undefined;
    }
  }
  getItem(product) {
    return this.getItems().find(item => {
      return item.getProduct().getId() === product.getId();
    });
  }
  isInCart(product) {
    return Boolean(this.getItem(product));
  }
  getProductQuantity(product) {
    const item = this.getItem(product);
    if (!item) return 0;
    return item.getQuantity();
  }
  getPrices() {
    return this.orderable.getPrices();
  }
  getDiscounts() {
    return this.orderable.getDiscounts();
  }
  getTaxDetails() {
    return this.orderable.getTaxDetails();
  }
  hasDiscount() {
    return this.orderable.hasDiscount();
  }
  getPromoCode() {
    return this.orderable.getPromoCode();
  }
  getPromoCodes() {
    return this.orderable.getPromoCodes();
  }
  getReward() {
    return this.orderable.getReward();
  }

  hasReward() {
    return !!this.getReward();
  }

  getPaymentOption() {
    return get(this.cartObject, "data.attributes.payment_option", "cash");
  }
  hasItems() {
    return Boolean(this.getItems().length);
  }
  clone() {
    const clonedCartObject = deepClone(this.cartObject);
    const clonedPricesObject = deepClone(this.cartPricesObject);
    return new TymberCart(clonedCartObject, clonedPricesObject);
  }

  pricesDirty() {
    return get(this.cartPricesObject, "dirty", false);
  }

  setPromoCodes(promoCodes) {
    const clonedCartObject = deepClone(this.cartObject);
    clonedCartObject.data.attributes.promo_codes = promoCodes;
    clonedCartObject.data.attributes.promo_code =
      promoCodes?.length > 0 ? promoCodes[0] : null;
    return new TymberCart(clonedCartObject, {...this.cartPricesObject, dirty: true});
  }

  setReward(reward) {
    const clonedCartObject = deepClone(this.cartObject);
    clonedCartObject.data.attributes.reward_id = reward ? reward.id : null;
    return new TymberCart(clonedCartObject, {...this.cartPricesObject, dirty: true});
  }

  setSchedule(schedule) {
    return this.setDeliverySpec({
      mode: schedule.code,
      scheduled_start_time: schedule.getStartTime8601(),
      scheduled_end_time: schedule.getEndTime8601(),
    });
  }

  setDeliverySpec(attrs) {
    const clonedCartObject = deepClone(this.cartObject);
    const deliverySpecRel = get(
      clonedCartObject,
      "data.relationships.delivery_specification.data"
    );
    if (deliverySpecRel) {
      const deliverySpec = findRelation(clonedCartObject.included, deliverySpecRel);
      deliverySpec.attributes = {
        ...deliverySpec.attributes,
        ...attrs,
      };
    }
    return new TymberCart(clonedCartObject, {...this.cartPricesObject, dirty: true});
  }

  setCartObject(cartObject) {
    return new TymberCart(cartObject, {...this.cartPricesObject, dirty: true});
  }
  setCartPrices(cartPrices) {
    return new TymberCart(this.cartObject, cartPrices);
  }

  getDeliverySpecification() {
    const deliverySpecRel = get(
      this.cartObject,
      "data.relationships.delivery_specification.data"
    );

    if (deliverySpecRel) {
      const deliverySpec = findRelation(this.cartObject.included, deliverySpecRel);
      return new DeliverySpecification(deliverySpec);
    }

    return null;
  }

  hasPromoCode() {
    return this.getPromoCode() || (this.getPromoCodes() || []).length > 0;
  }

  removePromoCodes(promoCodes) {
    if (Array.isArray(promoCodes)) {
      return this.getPromoCodes().filter(code => !promoCodes.includes(code));
    } else if (typeof promoCodes === "string") {
      return this.getPromoCodes().filter(
        c => c.toLowerCase() !== promoCodes.toLowerCase()
      );
    } else {
      return this.getPromoCodes();
    }
  }
}

export default TymberCart;
